import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportesFiltros, TaskService } from '../services/task/task.service';
import { ApiGeometry } from '../../models/Geometries';
import Task from '../../models/Task';
import { message } from 'antd';
import { RootState } from '../store';

export interface ReportsState {
  pointsIdsSelected: Set<number>;
  points: ApiGeometry[];
  tasks: Task[];
  loading: boolean;
  totalItems: number;
  filtros: ReportesFiltros;
  showFiltros: boolean;
  loadingWordReport: boolean;
  loadingExcelReport: boolean;
}

const initialState: ReportsState = {
  pointsIdsSelected: new Set(),
  points: [],
  tasks: [],
  loading: false,
  totalItems: 0,
  filtros: {
    search: '',
    categorias: [],
    page: 1,
    pageSize: 10,
    expediente: '',
    tipoFecha: 'realizacion',
    tipoTarea: [],
  },
  showFiltros: false,
  loadingWordReport: false,
  loadingExcelReport: false,
};

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    resetPointsSelected: (state) => {
      state.pointsIdsSelected.clear();
      state.points = [];
    },
    selectPoint: (state: ReportsState, arg: PayloadAction<ApiGeometry>) => {
      state.pointsIdsSelected.add(arg.payload.id);
      state.points.push(arg.payload);
    },
    unselectPoint: (state: ReportsState, arg: PayloadAction<ApiGeometry>) => {
      state.pointsIdsSelected.delete(arg.payload.id);
      state.points.splice(state.points.findIndex(p => p.id === arg.payload.id), 1);
    },
    selectTasks: (state: ReportsState, arg: PayloadAction<number[]>) => {
      const tasks = state.tasks.filter(t => arg.payload.includes(t.id!));
      tasks.forEach(task => {
        task.geometrias!.forEach(g => {
          state.pointsIdsSelected.add(g.id);
          state.points.push(g);
        });
      });
    },
    unselectTasks: (state: ReportsState, arg: PayloadAction<number[]>) => {
      const tasks = state.tasks.filter(t => arg.payload.includes(t.id!));
      tasks.forEach(task => {
        task.geometrias!.forEach(g => {
          state.pointsIdsSelected.delete(g.id);
          state.points.splice(state.points.findIndex(p => p.id === g.id), 1);
        });
      });
    },
    setReportsFilter: (state: ReportsState, arg: PayloadAction<Partial<ReportesFiltros>>) => {
      if (arg.payload.page === undefined) {
        state.filtros = { ...state.filtros, ...arg.payload, page: 1 };
      } else {
        state.filtros = { ...state.filtros, ...arg.payload };
      }
    },
    setShowFiltrosReports: (state: ReportsState, arg: PayloadAction<boolean>) => {
      state.showFiltros = arg.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getReportTasks.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getReportTasks.rejected, state => {
      state.loading = false;
    });
    builder.addCase(getReportTasks.fulfilled, (state, action) => {
      state.loading = false;
      state.tasks = action.payload.results;
      state.totalItems = action.payload.pagination.total;
    });
    builder.addCase(getWordReport.pending, (state, action) => {
      state.loadingWordReport = true;
    });
    builder.addCase(getWordReport.rejected, state => {
      state.loadingWordReport = false;
      message.error('Error al generar reporte.');
    });
    builder.addCase(getWordReport.fulfilled, (state, action) => {
      state.loadingWordReport = false;
    });
    builder.addCase(getReportsExcel.pending, (state, action) => {
      state.loadingExcelReport = true;
    });
    builder.addCase(getReportsExcel.rejected, state => {
      state.loadingExcelReport = false;
      message.error('Error al generar reporte.');
    });
    builder.addCase(getReportsExcel.fulfilled, (state, action) => {
      state.loadingExcelReport = false;
    });
  },
});

export const getReportTasks = createAsyncThunk(
  'getReportTasks',
  async (_: void, api) => {
    return TaskService.getReportTasks((api.getState() as RootState).reports.filtros);
  },
);

export const getReportsExcel = createAsyncThunk(
  'get_reports_export_excel',
  async (idsPoints: number[]) => {
    return TaskService.getReportsExcel(idsPoints);
  },
);

export const getWordReport = createAsyncThunk(
  'get_word_report',
  async (idsPoints: number[]) => {
    return TaskService.downloadWordReport(idsPoints);
  },
);

export const {
  setShowFiltrosReports, selectPoint, unselectPoint, selectTasks, unselectTasks, setReportsFilter,
} = reportsSlice.actions;

export default reportsSlice.reducer;
